import type { RecursivePartial } from '@freelancer/datastore';
import type {
  HtmlElement,
  RelatedItemsBlade,
  RichTextElement,
} from '@freelancer/html-renderer';
// This requires deep import in order to avoid transpiling
// html-renderer.module.ts from index.ts.
// eslint-disable-next-line local-rules/validate-freelancer-imports
import { HtmlElementType } from '@freelancer/html-renderer/html-elements.model';
import type {
  ContentCustomizationApi,
  RichTextElementApi,
} from 'api-typings/common/common';
import { RichTextElementTypeApi } from 'api-typings/common/common';
import type { ContentCustomization } from './content-customization.model';

export function transformContentCustomization(
  contentCustomization: ContentCustomizationApi,
): ContentCustomization {
  return {
    id: contentCustomization.id,
    entityId: contentCustomization.entity_id,
    entityType: contentCustomization.entity_type,
    injectionPoint: contentCustomization.injection_point,
    elements: contentCustomization.elements.map(transformRichTextElement),
    languageCode: contentCustomization.language_code,
  };
}

export function transformRichTextElement(
  richTextElement: RichTextElementApi,
): RichTextElement {
  // Typecasting from RichTextElementTypeApi to HTMLElementType with an exception
  // for TEXT because one's underlying value is text and another is #text
  if (
    !Object.values(HtmlElementType).includes(
      richTextElement.type as unknown as HtmlElementType,
    ) &&
    richTextElement.type !== RichTextElementTypeApi.TEXT
  ) {
    throw new Error(
      `Unsupported rich text element tag: ${richTextElement.type}`,
    );
  }

  const type =
    richTextElement.type === RichTextElementTypeApi.TEXT
      ? HtmlElementType.TEXT
      : richTextElement.type;

  if (
    type === RichTextElementTypeApi.RELATED_PORTFOLIOS ||
    type === RichTextElementTypeApi.RELATED_FREELANCERS
  ) {
    return {
      type:
        type === RichTextElementTypeApi.RELATED_PORTFOLIOS
          ? HtmlElementType.RELATED_PORTFOLIOS
          : HtmlElementType.RELATED_FREELANCERS,
      ids: richTextElement.related_elements_ids ?? [],
      title: richTextElement.blade_title,
    } as RelatedItemsBlade;
  }

  return {
    type,
    url: richTextElement.url,
    children: richTextElement.children?.map(transformRichTextElement),
    value: richTextElement.content,
    fileId: richTextElement.file_id,
    rel: richTextElement.rel,
    height: richTextElement.height,
    width: richTextElement.width,
    alt: richTextElement.alt,
    id: richTextElement.id,
  } as HtmlElement;
}

export function transformRichTextElementForApi(
  element: RecursivePartial<RichTextElement> | undefined,
): RichTextElementApi {
  if (element === undefined) {
    throw new Error('Element is undefined');
  }
  // Typecasting from HTMLElementType to RichTextElementTypeApi with an exception
  // for TEXT because one's underlying value is #text and another is text
  if (
    !Object.values(RichTextElementTypeApi).includes(
      element.type as unknown as RichTextElementTypeApi,
    ) &&
    element.type !== HtmlElementType.TEXT
  ) {
    throw new Error(`Unsupported rich text element tag: ${element.type}`);
  }

  const type =
    element.type === HtmlElementType.TEXT
      ? RichTextElementTypeApi.TEXT
      : element.type;

  if (
    type === HtmlElementType.RELATED_PORTFOLIOS ||
    type === HtmlElementType.RELATED_FREELANCERS
  ) {
    return {
      type:
        type === HtmlElementType.RELATED_PORTFOLIOS
          ? RichTextElementTypeApi.RELATED_PORTFOLIOS
          : RichTextElementTypeApi.RELATED_FREELANCERS,
      related_elements_ids: 'ids' in element ? element.ids : [],
      blade_title: 'title' in element ? element.title : undefined,
    } as RichTextElementApi;
  }

  return {
    type,
    url: 'url' in element ? element.url : undefined,
    children:
      'children' in element
        ? element.children?.map(child => {
            return transformRichTextElementForApi(
              child as RecursivePartial<RichTextElement>,
            );
          })
        : undefined,
    content: 'value' in element ? element.value : undefined,
    file_id: 'fileId' in element ? element.fileId : undefined,
    rel: 'rel' in element ? element.rel : undefined,
    height: 'height' in element ? element.height : undefined,
    width: 'width' in element ? element.width : undefined,
    alt: 'alt' in element ? element.alt : undefined,
    id: 'id' in element ? element.id : undefined,
  } as RichTextElementApi;
}
